import type { SpecimenPurpose } from '../labmatrix/Labmatrix.types';
import type { PatientTimelineEventKind } from './PatientTimeline.types';

export const LOADING_TEXT = {
  TITLE: 'LOADING...',
  SUBTITLE: 'Give us a moment. The data will be ready shortly.',
} as const;

const FALLBACK_EVENT_KIND: PatientTimelineEventKind = 'Other';

/** A sequenced event code such as `D1` or `P3`, matched against the lower-cased event. */
const SEQUENCED_EVENT_CODE_PATTERN = /^([a-z])[0-9]$/;

interface IEventKindRule {
  kind: PatientTimelineEventKind;
  keyword: string;
  codeLetter?: string;
}

const EVENT_KIND_RULES: readonly IEventKindRule[] = [
  { kind: 'Diagnosis', keyword: 'diagnosis', codeLetter: 'd' },
  { kind: 'Progression', keyword: 'progression', codeLetter: 'p' },
  { kind: 'Relapse', keyword: 'relapse', codeLetter: 'r' },
  { kind: 'Death', keyword: 'death' },
];

const SPECIMEN_PURPOSE_ABBREVIATIONS: Record<SpecimenPurpose, string> = {
  'Germline - donor': 'GD',
  'Germline - patient': 'GP',
  'Liquid Biopsy': 'LB',
  'TB Banked': 'TBB',
  'TB ZERO Potentials': 'ZERO1',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- LabMatrix's own key
  Tumour: 'TT',
};

const UNKNOWN_SPECIMEN_PURPOSE_ABBREVIATION = 'UN';

export const getPatientTimelineEventKind = (event?: string): PatientTimelineEventKind => {
  const normalisedEvent = event?.toLowerCase() ?? '';
  const [, codeLetter] = normalisedEvent.match(SEQUENCED_EVENT_CODE_PATTERN) ?? [];

  return EVENT_KIND_RULES.find(({ codeLetter: ruleCodeLetter, keyword }) => (
    (codeLetter !== undefined && ruleCodeLetter === codeLetter)
    || normalisedEvent.includes(keyword)
  ))?.kind ?? FALLBACK_EVENT_KIND;
};

export const getSpecimenPurposeAbbreviation = (
  specimenPurpose?: SpecimenPurpose | null,
): string => (
  specimenPurpose && Object.hasOwn(SPECIMEN_PURPOSE_ABBREVIATIONS, specimenPurpose)
    ? SPECIMEN_PURPOSE_ABBREVIATIONS[specimenPurpose]
    : UNKNOWN_SPECIMEN_PURPOSE_ABBREVIATION
);
